import "./App.scss";
import "./index.css";
import { useCallback, useEffect, useState } from "react";
import { useTracker } from "@/context/TrackerContext";
import { useIsOpenCity, usePageState } from "@/context/PageStateContext";
import { PrefetchDataProvider } from "@/context/PrefetchDataContext";
import { useInitPageState } from "@/hooks/useInitPageState";
import NavigationHeader from "@/components/navigationHeader";
import CouponWall from "@/components/couponWall";
import SupplyArea from "./components/SupplyArea";
import { useAppEnv } from "@/hooks/useAppEnv";
import { SKELETON_LOGO_URL } from "@/lib/images";
import { initUuidTracker, initUuidFromSSR } from "@/lib/uuid";
import hideNavigationBar from "@/lib/bridge/hideNavigationBar";
import { reportCustomMetric, setOwlReady, flushCustomMetricsQueue } from "@/lib/owl";
import exchangePtcForUnpl from "@/lib/ptcToUnplConverter";
import { getUserId } from "@/lib/userInfo";
import recordStep from "@/lib/bridge/recordStep";
import { PrefetchData } from "@/types/serverSideProps";
import useResource from "@/hooks/useResource";
import { COUPON_WALL_STYLE, DEFAULT_BG_IMG, RESOURCE_SCENE, UNAVAILABLE_IMG } from "./constants";
import { CouponWallScene } from "@/types/coupon-wall";
import { LX_CONFIG, PAGE_CID } from "@/track/waimai";

interface AppProps {
  _prefetchData?: PrefetchData;
}

export default function WaimaiPage(props: AppProps) {
  // 从props中获取服务端预取数据
  const { _prefetchData } = props;
  const tracker = useTracker();
  const reportPV = useCallback(async () => {
    if (!tracker) {
      return;
    }
    const userId = getUserId();
    if (userId) tracker.setUserId(userId);
    const sessionId = (await tracker?.getSessionId().catch(() => "")) || "";
    tracker.pageView({
      event_timestamp: Date.now(),
      session_id: sessionId,
    });
  }, [tracker]);

  useEffect(() => {
    if (!tracker) {
      return;
    }

    reportPV();
    // 上报容器性能埋点
    recordStep({ stepName: "web_action_reportPV" });
    reportCustomMetric({
      key: "pageInitCount",
      value: 1,
    });
    if (!_prefetchData?.uuid) {
      // 启动 tracker 的 getUuid 请求（不阻塞），供 getUuid() 在需要时同步读取
      initUuidTracker(tracker);
    }
  }, [tracker]);

  // SSR/CSR 通用：若有服务端下发的 uuid 则注入到 uuid 模块缓存，getUuid() 将直接复用
  initUuidFromSSR(_prefetchData?.uuid ?? null);

  // 初始化页面状态（自动处理 SSR/CSR 差异）
  useInitPageState();
  // 使用 useAppEnv Hook 兼容 SSR/CSR 环境检测
  const appEnv = useAppEnv();
  const { isOpenCity } = useIsOpenCity();
  const { headBanner, isInitFinished } = useResource({ resourceScene: RESOURCE_SCENE });
  // 容器导航栏是否已经隐藏，用于控制样式
  const [isNavigationBarHidden, setIsNavigationBarHidden] = useState(appEnv.isGrowthContainer);
  const { setCompleteGetUnpl } = usePageState();

  // 标记 Owl 已就绪并批量上报队列中的自定义指标
  useEffect(() => {
    // 标记 Owl 已就绪，后续调用 reportCustomMetric 将直接上报
    setOwlReady();
    // 批量上报队列中存储的自定义指标
    flushCustomMetricsQueue();
  }, []);

  // APP 环境隐藏导航栏
  useEffect(() => {
    if (appEnv.isMT) {
      // 外投容器默认隐藏导航栏
      if (appEnv.isGrowthContainer) {
        setIsNavigationBarHidden(true);
      } else {
        hideNavigationBar({
          onSuccess() {
            if (appEnv.isIos) setIsNavigationBarHidden(true);
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    // 获取 unpl
    exchangePtcForUnpl(tracker, setCompleteGetUnpl, appEnv, _prefetchData);
    // 上报 CSR 性能埋点：骨架屏渲染完成时间
    if ((window as any)?.sendPerfPoint && _prefetchData) {
      (window as any).sendPerfPoint({
        name: "web.csr.fcp",
        timeStamp: Date.now(),
        position: 16,
      });
    }
  }, []);

  return (
    // @ts-ignore
    <PrefetchDataProvider value={{ prefetchData: _prefetchData }}>
      <div className={`page-container ${isNavigationBarHidden ? "is-hide-navigation-bar" : ""}`}>
        {/* 定位条 */}
        <div className={`page-header ${isNavigationBarHidden ? 'page-header-app' : ''} ${appEnv.isGrowthContainer && appEnv.isIos ? 'is-growth-container' : ''}`}>
          <NavigationHeader
            showSearchBar={false}
            navTextColor="#111111"
            enableNavBarBgOpacity
            lxConfig={LX_CONFIG}
          />
        </div>
        {isOpenCity
          ? <>
            <div className={`page-content ${isNavigationBarHidden ? "page-content-app" : ""}`}>
              {/* 头图区域 */}
              <div className="page-banner">
                {isInitFinished ? (
                  <img src={headBanner?.imageUrl || DEFAULT_BG_IMG} className="page-content-bg" />
                ) : (
                  <img src={SKELETON_LOGO_URL} className="page-banner-logo" />
                )}
              </div>
              {/* 红包墙 */}
              <div className="red-package-container">
                <CouponWall
                  pageCid={PAGE_CID}
                  scene={CouponWallScene.WAIMAI}
                  // 开启少量优惠券样式适配
                  enableFewCouponStyleFit
                  // 红包墙非空状态样式
                  couponWallStyle={COUPON_WALL_STYLE}
                />
              </div>
            </div>
            {/* 供给区域 */}
            <SupplyArea />
          </>
          : <div className="unavailable-page-container">
            <img className="unavailable-img" src={UNAVAILABLE_IMG} />
            <div className="unavailable-tip">更多精彩，即将上线，敬请期待</div>
          </div>
        }
      </div>
    </PrefetchDataProvider>
  );
}
